/**
 * @file Set album folder onboarding permissions.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import qs from 'query-string';

import { AlbumFolderWithCreatorBrandDetails } from '../../../graphql/queries/album-folder';
import {
  setJoinTypeDefault as setJoinTypeDefaultAction,
  setJoinTypeSelectionFlag as setJoinTypeSelectionFlagAction,
} from '../../../actions/discover-album-folder';
import { ALBUM_PRICE_MODE } from '../../../constants/albums';
import { PPGD_JOIN_TYPE } from '../../../constants/discover-album-folder';
import {
  allowsJoinTypeSelection,
  getDefaultJoinType,
} from '../../../util/discover-album-folder';
import { ALBUM_FOLDER_JOIN_TYPE } from '../../../constants/album-folder';

/**
 * This Higher Order Component is responsible for setting up defaults
 * regarding how a user should be onboarding a certain Album Folder.
 *
 * 1. Sets album folder onboarding join type (purchase or join):
 *
 * If an album folder contains only PPD albums the user
 * should be able to only join those albums (not purchasing them), or
 * if the album folder contains only PPA albums or the folder itself is PPA
 * (PPG) the user should only be able to purchase them.
 *
 * The default join type for cases where PPA and PPD co-exist is JOIN (except
 * for other cases where we force it otherwise i.e. If coming from a landing page),
 * and the user can select to buy afterwards.
 *
 * 2: Sets whether the user can edit their join type selection (switch
 * from purchase to join and vice-versa).
 *
 * The below table demonstrates the possible pricing configurations and their
 * expected outcomes/defaults:
 *
 * ==================================================================
 *  Pricing Configuration  |      Default      |    User Can Edit
 *    Folder  |   Albums   |     Join Type     |    Choice Later?
 * ==================================================================
 *     PPG    |   NONE     |     PURCHASE      |        No
 * ------------------------------------------------------------------.
 *     PPG    |   PPD      |       JOIN        |        Yes
 * ==================================================================
 *     NONE   |   PPA      |     PURCHASE      |        No
 * ------------------------------------------------------------------.
 *     NONE   |   PPA/D    |       JOIN        |        Yes
 * ------------------------------------------------------------------.
 *     NONE   |   PPD      |       JOIN        |        No
 * ------------------------------------------------------------------.
 *
 * Note: If an AlbumFolder is marked with `force_join_without_purchase: true`
 * and has PPA/G and PPD options or PPA drip we're disabling the PURCHASE
 * join type and forcing them to JOIN.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithSetAlbumFolderJoinPreferencesHOC = WrappedComponent =>
  class WithSetAlbumFolderJoinPreferences extends Component {
    static propTypes = {
      albumFolder: PropTypes.shape({
        album_price_mode: PropTypes.oneOf(Object.values(ALBUM_PRICE_MODE)),
        price: PropTypes.shape({
          value: PropTypes.number,
        }),
        force_join_without_purchase: PropTypes.bool,
        join_without_purchase: PropTypes.bool,
        join_type: PropTypes.string,
      }),
      location: PropTypes.shape({
        search: PropTypes.string,
      }).isRequired,
      setJoinTypeDefault: PropTypes.func.isRequired,
      setJoinTypeSelectionFlag: PropTypes.func.isRequired,
    };

    componentDidMount() {
      const { albumFolder } = this.props;

      const rootAlbumFolder = albumFolder?.root ?? albumFolder;

      if (
        this.albumFolderDataExist(rootAlbumFolder) &&
        !rootAlbumFolder?.parent
      ) {
        this.setAlbumFolderJoinPreferences();
      }
    }

    componentDidUpdate(prevProps) {
      const { albumFolder } = this.props;
      const rootAlbumFolder = albumFolder?.root ?? albumFolder;
      const prevRootAlbumFolder =
        prevProps.albumFolder?.root ?? prevProps.albumFolder;

      if (
        // Ensure we only play with root album folders.
        !rootAlbumFolder?.parent &&
        !this.albumFolderDataExist(prevRootAlbumFolder) &&
        this.albumFolderDataExist(rootAlbumFolder)
      ) {
        this.setAlbumFolderJoinPreferences();
      }
    }

    /**
     * Checks if we have the required data to set the album folder join
     * preferences. This is important to check as the AlbumFolder object
     * might exist in the cache from a different query without containing
     * the required data.
     *
     * @param {Object} albumFolder - The AlbumFolder.
     * @returns {boolean}
     */
    albumFolderDataExist = albumFolder => {
      if (!albumFolder) {
        return false;
      }

      return (
        'join_without_purchase' in albumFolder &&
        'album_price_mode' in albumFolder
      );
    };

    setAlbumFolderJoinPreferences = () => {
      const {
        albumFolder,
        location,
        setJoinTypeDefault,
        setJoinTypeSelectionFlag,
      } = this.props;

      const rootAlbumFolder = albumFolder?.root ?? albumFolder;

      const { joinType } = qs.parse(location.search);
      const allowsJoinTypeSelectionBool =
        allowsJoinTypeSelection(rootAlbumFolder);

      setJoinTypeSelectionFlag(allowsJoinTypeSelectionBool);

      // If the album folder forces JOINs set to join and return.
      if (rootAlbumFolder.force_join_without_purchase) {
        setJoinTypeDefault(PPGD_JOIN_TYPE.JOIN);
        return;
      }

      // Search subs / credits joins are joins-only, not purchase perse (TBConfirmed)
      if (rootAlbumFolder.search_subscription_credits_joins_enabled) {
        setJoinTypeDefault(PPGD_JOIN_TYPE.JOIN);
        return;
      }

      if (
        (rootAlbumFolder.album_price_mode === ALBUM_PRICE_MODE.PPA_ONLY &&
          rootAlbumFolder.join_type === ALBUM_FOLDER_JOIN_TYPE.INDIVIDUAL) ||
        (rootAlbumFolder.price.value > 0 &&
          rootAlbumFolder.album_price_mode === ALBUM_PRICE_MODE.NONE)
      ) {
        // Sell Photos behavior has been updated due to pre-pay.
        if (rootAlbumFolder.picture_day_enabled) {
          setJoinTypeDefault(
            getDefaultJoinType(rootAlbumFolder) ?? PPGD_JOIN_TYPE.JOIN,
          );
          return;
        }
        // The below rules must reflect the table in the component's
        // documentation above.
        setJoinTypeDefault(PPGD_JOIN_TYPE.PURCHASE);
        return;
      }

      // Handle forced default selection from URL query string
      // (only if the album folder supports it according to the config rules)
      if (joinType && allowsJoinTypeSelectionBool) {
        if (joinType === 'purchase') {
          setJoinTypeDefault(PPGD_JOIN_TYPE.PURCHASE);

          // Explicitely prevent the user from making a choice as they've
          // selected their join type preference in the previous screen
          setJoinTypeSelectionFlag(false);
        }

        if (joinType === 'join') {
          setJoinTypeDefault(PPGD_JOIN_TYPE.JOIN);
          setJoinTypeSelectionFlag(false);
        }

        return;
      }

      setJoinTypeDefault(PPGD_JOIN_TYPE.JOIN);
    };

    render() {
      return <WrappedComponent {...this.props} />;
    }
  };

/**
 * Higher order container for setting the album folder join preferences.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const SetAlbumFolderJoinPreferences = WrappedComponent => {
  const WithSetAlbumFolderJoinPreferences =
    WithSetAlbumFolderJoinPreferencesHOC(WrappedComponent);

  const mapStateToProps = ({ discoverAlbumFolder }) => ({
    albumFolderId: discoverAlbumFolder.albumFolderId,
  });

  const mapDispatchToProps = {
    setJoinTypeDefault: setJoinTypeDefaultAction,
    setJoinTypeSelectionFlag: setJoinTypeSelectionFlagAction,
  };

  const withAlbumFolderData = graphql(AlbumFolderWithCreatorBrandDetails, {
    options: ({ albumFolderId }) => ({
      variables: {
        id: albumFolderId,
      },
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loading,
    }),
    skip: ({ albumFolderId }) => !albumFolderId,
  });

  return compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
    withAlbumFolderData,
  )(WithSetAlbumFolderJoinPreferences);
};

export { WithSetAlbumFolderJoinPreferencesHOC };
export default SetAlbumFolderJoinPreferences;
